import React, { useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from 'context/AuthContext';
import { fetchNotificationsSummary, publishIncomingNotifications } from 'helpers/notifications';
import { fetchMessagesSummary, subscribeMessageStream } from 'helpers/messages';
import newSellSound from 'assets/sounds/new-sell.wav';
import newMessageSound from 'assets/sounds/new-message.wav';
import newQuestionSound from 'assets/sounds/new-question.wav';

const BACKGROUND_POLL_INTERVAL_MS = 20000;
const VISIBLE_POLL_INTERVAL_MS = 20000;
const LAST_SEEN_STORAGE_PREFIX = 'backgroundNotificationsLastSeen';
const PERMISSION_REQUESTED_PREFIX = 'backgroundNotificationsPermissionAsked';

const getStorageKey = (prefix, userId) => `${prefix}:${userId || 'anonymous'}`;

const getStoredLastSeenId = userId => {
  if (typeof window === 'undefined') {
    return 0;
  }

  try {
    return Number(window.localStorage.getItem(getStorageKey(LAST_SEEN_STORAGE_PREFIX, userId)) || 0);
  } catch (error) {
    return 0;
  }
};

const storeLastSeenId = (userId, notificationId) => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(
      getStorageKey(LAST_SEEN_STORAGE_PREFIX, userId),
      String(Number(notificationId || 0))
    );
  } catch (error) {
    return;
  }
};

const getPermissionRequested = userId => {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    return window.localStorage.getItem(getStorageKey(PERMISSION_REQUESTED_PREFIX, userId)) === 'true';
  } catch (error) {
    return false;
  }
};

const setPermissionRequested = userId => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(getStorageKey(PERMISSION_REQUESTED_PREFIX, userId), 'true');
  } catch (error) {
    return;
  }
};

const shouldNotifyInBackground = () => {
  if (typeof document === 'undefined') {
    return false;
  }
  return document.hidden || (typeof document.hasFocus === 'function' && !document.hasFocus());
};

const resolveSoundKey = notification => {
  const category = String(notification?.category || '').toLowerCase();
  const topic = String(notification?.topic || '').toLowerCase();

  if (category === 'questions' || topic === 'questions') {
    return 'question';
  }

  if (
    category === 'sales' ||
    topic === 'orders' ||
    topic === 'orders_v2' ||
    topic === 'created_orders' ||
    topic === 'payments' ||
    topic === 'shipments'
  ) {
    return 'sale';
  }

  return 'message';
};

const buildMessageBrowserNotificationContent = conversation => {
  const source = String(conversation?.source || '').toLowerCase();
  const senderName = conversation?.latest_message?.sender_name || '';
  const conversationTitle = conversation?.title || '';
  const itemTitle = conversation?.item_title || '';
  const preview = conversation?.last_message_preview || conversation?.latest_message?.body || '';
  const isGroup = Boolean(conversation?.is_group);

  let title = conversationTitle || 'Nuevo mensaje';
  const bodyParts = [];

  if (senderName) {
    title = isGroup
      ? `${senderName} en ${conversationTitle || 'Grupo'}`
      : senderName;
  }

  if (source === 'mercadolibre') {
    if (itemTitle) {
      bodyParts.push(itemTitle);
    }
    if (preview) {
      bodyParts.push(preview);
    }
  } else {
    if (!senderName && conversationTitle) {
      bodyParts.push(conversationTitle);
    }
    if (preview) {
      bodyParts.push(preview);
    }
  }

  return {
    title,
    body: bodyParts.filter(Boolean).join('\n') || preview || conversationTitle || 'Nuevo mensaje'
  };
};

const getConversationRoute = conversation => {
  const source = conversation?.source || 'internal';
  return source === 'mercadolibre'
    ? `/mercadolibre-messages?conversation=${conversation?.id}&source=${source}`
    : `/messages?conversation=${conversation?.id}&source=${source}`;
};

const BackgroundNotificationsBridge = () => {
  const location = useLocation();
  const { isAuthenticated, hasAnyPermission, userData } = useAuth();
  const userId = userData?.userId;
  const isMessagesRoute =
    location.pathname.startsWith('/messages') ||
    location.pathname.startsWith('/mercadolibre-messages');
  const canViewNotifications = hasAnyPermission([
    'notifications.view_internal',
    'notifications.view_mercadolibre',
    'notifications.view_questions'
  ]);

  const soundSources = useMemo(
    () => ({
      sale: newSellSound,
      message: newMessageSound,
      question: newQuestionSound
    }),
    []
  );

  const audioElementsRef = useRef({});
  const lastSeenIdRef = useRef(0);
  const seededRef = useRef(false);
  const pollTimeoutRef = useRef(null);
  const interactionUnlockedRef = useRef(false);
  const lastMessageNotificationKeyRef = useRef('');
  const pushChannelActiveRef = useRef(false);

  useEffect(() => {
    let mounted = true;

    const refreshPushChannelState = async () => {
      if (typeof window === 'undefined' || !('serviceWorker' in navigator) || !('PushManager' in window)) {
        if (mounted) {
          pushChannelActiveRef.current = false;
        }
        return;
      }

      try {
        const registration = await navigator.serviceWorker.getRegistration('/push-sw.js');
        const subscription = registration ? await registration.pushManager.getSubscription() : null;
        if (mounted) {
          pushChannelActiveRef.current = Boolean(subscription);
        }
      } catch (error) {
        if (mounted) {
          pushChannelActiveRef.current = false;
        }
      }
    };

    refreshPushChannelState();
    window.addEventListener('focus', refreshPushChannelState);

    return () => {
      mounted = false;
      window.removeEventListener('focus', refreshPushChannelState);
    };
  }, []);

  useEffect(() => {
    audioElementsRef.current = {
      sale: new Audio(soundSources.sale),
      message: new Audio(soundSources.message),
      question: new Audio(soundSources.question)
    };

    Object.keys(audioElementsRef.current).forEach(key => {
      const audio = audioElementsRef.current[key];
      audio.preload = 'auto';
    });

    return () => {
      Object.keys(audioElementsRef.current).forEach(key => {
        const audio = audioElementsRef.current[key];
        if (audio) {
          audio.pause();
          audio.src = '';
        }
      });
      audioElementsRef.current = {};
    };
  }, [soundSources]);

  useEffect(() => {
    if (!isAuthenticated() || !canViewNotifications) {
      return undefined;
    }

    const unlockAudioAndRequestPermission = () => {
      if (!interactionUnlockedRef.current) {
        interactionUnlockedRef.current = true;
        Object.keys(audioElementsRef.current).forEach(key => {
          const audio = audioElementsRef.current[key];
          if (!audio) {
            return;
          }
          const previousMutedState = audio.muted;
          audio.muted = true;
          audio.play()
            .then(() => {
              audio.pause();
              audio.currentTime = 0;
              audio.muted = previousMutedState;
            })
            .catch(() => {
              audio.muted = previousMutedState;
            });
        });
      }

      if (
        typeof window !== 'undefined' &&
        'Notification' in window &&
        window.Notification.permission === 'default' &&
        !getPermissionRequested(userId)
      ) {
        setPermissionRequested(userId);
        window.Notification.requestPermission().catch(() => {});
      }
    };

    const eventOptions = { passive: true };
    window.addEventListener('pointerdown', unlockAudioAndRequestPermission, eventOptions);
    window.addEventListener('keydown', unlockAudioAndRequestPermission, eventOptions);
    window.addEventListener('touchstart', unlockAudioAndRequestPermission, eventOptions);

    return () => {
      window.removeEventListener('pointerdown', unlockAudioAndRequestPermission, eventOptions);
      window.removeEventListener('keydown', unlockAudioAndRequestPermission, eventOptions);
      window.removeEventListener('touchstart', unlockAudioAndRequestPermission, eventOptions);
    };
  }, [canViewNotifications, isAuthenticated, userId]);

  useEffect(() => {
    if (!isAuthenticated() || !canViewNotifications || !userId) {
      return undefined;
    }

    let cancelled = false;
    lastSeenIdRef.current = getStoredLastSeenId(userId);
    seededRef.current = false;

    const playNotificationSound = notification => {
      const audio = audioElementsRef.current[resolveSoundKey(notification)];
      if (!audio) {
        return;
      }

      try {
        audio.pause();
        audio.currentTime = 0;
      } catch (error) {
        return;
      }

      audio.play().catch(() => {});
    };

    const showBrowserNotification = notification => {
      if (
        typeof window === 'undefined' ||
        !('Notification' in window) ||
        window.Notification.permission !== 'granted' ||
        pushChannelActiveRef.current
      ) {
        return;
      }

      const browserNotification = new window.Notification(notification.title || 'Gestodo', {
        body: notification.message || '',
        icon: `${window.location.origin}/favicon.ico`,
        tag: `gestodo-notification-${notification.id}`
      });

      browserNotification.onclick = () => {
        window.focus();
        if (notification.link) {
          window.location.href = notification.link;
        }
        browserNotification.close();
      };
    };

    const pollNotifications = async () => {
      try {
        const payload = await fetchNotificationsSummary();
        if (cancelled) {
          return;
        }

        const notifications = Array.isArray(payload?.notifications) ? payload.notifications : [];
        const highestId = notifications.reduce(
          (maxValue, item) => Math.max(maxValue, Number(item?.id || 0)),
          0
        );

        if (!seededRef.current) {
          const seededId = Math.max(lastSeenIdRef.current, highestId);
          lastSeenIdRef.current = seededId;
          storeLastSeenId(userId, seededId);
          seededRef.current = true;
          return;
        }

        const newNotifications = notifications
          .filter(item => Number(item?.id || 0) > Number(lastSeenIdRef.current || 0))
          .sort((left, right) => Number(left?.id || 0) - Number(right?.id || 0));

        if (newNotifications.length) {
          publishIncomingNotifications(newNotifications);
        }

        if (newNotifications.length && shouldNotifyInBackground()) {
          const latestNotification = newNotifications[newNotifications.length - 1];
          playNotificationSound(latestNotification);
          showBrowserNotification(latestNotification);
        }

        if (highestId > Number(lastSeenIdRef.current || 0)) {
          lastSeenIdRef.current = highestId;
          storeLastSeenId(userId, highestId);
        }
      } catch (error) {
        console.error('Error polling background notifications:', error);
      }
    };

    const schedulePoll = async (runImmediately = false) => {
      if (cancelled) {
        return;
      }

      if (runImmediately) {
        await pollNotifications();
      }

      if (pollTimeoutRef.current) {
        window.clearTimeout(pollTimeoutRef.current);
      }

      pollTimeoutRef.current = window.setTimeout(() => {
        schedulePoll(true);
      }, shouldNotifyInBackground() ? BACKGROUND_POLL_INTERVAL_MS : VISIBLE_POLL_INTERVAL_MS);
    };

    const handleVisibilityChange = () => {
      schedulePoll(true);
    };

    schedulePoll(true);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleVisibilityChange);

    return () => {
      cancelled = true;
      if (pollTimeoutRef.current) {
        window.clearTimeout(pollTimeoutRef.current);
        pollTimeoutRef.current = null;
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleVisibilityChange);
    };
  }, [canViewNotifications, isAuthenticated, userId]);

  useEffect(() => {
    if (!isAuthenticated() || !userId) {
      return undefined;
    }

    const canViewMessages =
      hasAnyPermission(['messages.view_internal', 'messages.view_mercadolibre']);
    if (!canViewMessages || isMessagesRoute) {
      return undefined;
    }

    fetchMessagesSummary().catch(() => {});

    const playMessageSound = conversation => {
      const soundKey = conversation?.source === 'mercadolibre' ? 'message' : 'message';
      const audio = audioElementsRef.current[soundKey];
      if (!audio) {
        return;
      }

      try {
        audio.pause();
        audio.currentTime = 0;
      } catch (error) {
        return;
      }

      audio.play().catch(() => {});
    };

    const showMessageNotification = conversation => {
      if (
        typeof window === 'undefined' ||
        !('Notification' in window) ||
        window.Notification.permission !== 'granted' ||
        pushChannelActiveRef.current
      ) {
        return;
      }

      const messageNotification = buildMessageBrowserNotificationContent(conversation);
      const browserNotification = new window.Notification(messageNotification.title, {
        body: messageNotification.body,
        icon: `${window.location.origin}/favicon.ico`,
        tag: `gestodo-message-${conversation?.id || 'unknown'}`
      });

      browserNotification.onclick = () => {
        window.focus();
        const source = conversation?.source || 'internal';
        const nextUrl =
          source === 'mercadolibre'
            ? `/mercadolibre-messages?conversation=${conversation?.id}&source=${source}`
            : `/messages?conversation=${conversation?.id}&source=${source}`;
        window.location.href = nextUrl;
        browserNotification.close();
      };
    };

    const showMessageToast = conversation => {
      toast.info(
        <div className="d-flex flex-column">
          <span className="fw-semi-bold">
            {conversation?.title || 'Nuevo mensaje'}
          </span>
          <span className="fs--1">
            {conversation?.last_message_preview || conversation?.latest_message?.body || ''}
          </span>
        </div>,
        {
          theme: 'colored',
          onClick: () => {
            window.location.href = getConversationRoute(conversation);
          }
        }
      );
    };

    const handleConversationUpdated = async payload => {
      const isOwnInternalMessage =
        payload?.source === 'internal' &&
        Number(payload?.sender_user_id) === Number(userId);

      if (
        isOwnInternalMessage ||
        payload?.system_notification ||
        payload?.group_updated ||
        Number(payload?.new_messages || 0) <= 0
      ) {
        return;
      }

      try {
        const summary = await fetchMessagesSummary();
        const conversation = (summary?.conversations || []).find(
          item => Number(item?.id) === Number(payload?.conversation_id)
        );
        if (!conversation) {
          return;
        }

        const notificationKey = [
          conversation.id,
          conversation.latest_message?.id || '',
          conversation.latest_message?.sent_at || conversation.last_message_at || ''
        ].join(':');

        if (
          !notificationKey ||
          notificationKey === lastMessageNotificationKeyRef.current
        ) {
          return;
        }

        lastMessageNotificationKeyRef.current = notificationKey;
        playMessageSound(conversation);

        if (shouldNotifyInBackground()) {
          showMessageNotification(conversation);
        } else {
          showMessageToast(conversation);
        }
      } catch (error) {
        console.error('Error resolving incoming message notification:', error);
      }
    };

    const eventSource = subscribeMessageStream({
      onConversationUpdated: handleConversationUpdated
    });

    return () => {
      eventSource.close();
    };
  }, [hasAnyPermission, isAuthenticated, isMessagesRoute, userId]);

  return null;
};

export default BackgroundNotificationsBridge;
